import { toArrayFromIterable } from '@utils/toArrayFromIterable';
import { computed, makeObservable, observable, override } from 'mobx';
import uniqBy from 'lodash/uniqBy';

import { EntryTypeData, EntryTypeListItemData, EntryTypeStatus } from 'chaika-frontend-types/frontend-cms';
import { EntryTypeType } from 'chaika-enums';
import { Optional } from 'chaika-common-types';

import { EntryType } from './EntryType';
import { EntryTypeBlockList } from './EntryTypeBlockList';

import { LongList } from '@store/abstract/LongList';

export interface EntryTypeFilterParams {
    statuses?: EntryTypeStatus[];
    types?: EntryTypeType[];
    soapIds?: string[];
    identity?: { id: string; version: number }[];
}

export class EntryTypeList extends LongList<EntryType, EntryTypeListItemData, EntryTypeFilterParams> {
    public static Name = 'entryTypeList' as const;
    public readonly limit: number = 50;

    @observable public archivedEntryTypes: Map<string, EntryType> = new Map();
    @observable public blockList: EntryTypeBlockList = new EntryTypeBlockList();

    constructor(params?: EntryTypeListItemData[]) {
        super(params);
        makeObservable(this);

        this.filterParams = {
            statuses: [],
            types: [],
        };
    }

    @computed
    public get activeEntryTypes(): EntryType[] {
        return this.values.filter(
            entryType => !entryType.isSystem && entryType.status === EntryTypeStatus.Active && !entryType.isNested,
        );
    }

    @computed
    public get activeNestedEntryTypes(): EntryType[] {
        return this.values.filter(entryType => entryType.status === EntryTypeStatus.Active && entryType.isNested);
    }

    @override
    public set(params: EntryTypeListItemData[]): void {
        const activeEntryTypes = params.filter(param => param.status === EntryTypeStatus.Active);
        const archivedEntryTypes = params.filter(param => param.status === EntryTypeStatus.Archived);

        super.set(activeEntryTypes);

        archivedEntryTypes.forEach(entryType => {
            this.archivedEntryTypes.set(
                `${entryType.id}_${entryType.version}`,
                new EntryType(entryType as EntryTypeData),
            );
        });
    }

    @override
    public add(params: EntryTypeListItemData[]): void {
        const activeEntryTypes = params.filter(param => param.status === EntryTypeStatus.Active);
        const archivedEntryTypes = params.filter(param => param.status === EntryTypeStatus.Archived);

        super.add(activeEntryTypes);

        archivedEntryTypes.forEach(entryType => {
            this.archivedEntryTypes.set(
                `${entryType.id}_${entryType.version}`,
                new EntryType(entryType as EntryTypeData),
            );
        });
    }

    public reset(): void {
        super.reset();
    }

    public getUniqEntryTypes(): EntryType[] {
        return uniqBy(
            [
                ...this.values.filter(entryType => !entryType.isSystem && !entryType.isNested),
                ...toArrayFromIterable(this.archivedEntryTypes),
            ],
            entryType => entryType.id,
        );
    }

    public getVersion(id: string, version: number): Optional<EntryType> {
        const entryType = this.get(id);

        return entryType && entryType.version === version ? entryType : this.archivedEntryTypes.get(`${id}_${version}`);
    }

    protected create(params: EntryTypeData): EntryType {
        return new EntryType(params as EntryTypeData);
    }

    protected filterValue(
        entryType: EntryType,
        { statuses = [], types = [], identity = [], soapIds = [] }: EntryTypeFilterParams,
    ): boolean {
        return (
            (soapIds.length > 0 && entryType.type === EntryTypeType.System ? soapIds.includes(entryType.id) : true) &&
            (statuses.length > 0 ? entryType.status !== null && statuses.includes(entryType.status) : true) &&
            (types.length > 0 ? types.includes(entryType.type) : true) &&
            (identity.length > 0
                ? identity.some(({ id, version }) => entryType.id === id && entryType.version === version)
                : true)
        );
    }
}

export const entryTypeList = new EntryTypeList();
